// ─────────────────────────────────────────────────────────────────────────────
// lazyScreen, deploy-safe code-splitting + honest crash recovery.
// (chunk-recovery 2026-07-17; crash-visibility 2026-07-18, Ryan: "we randomly
// get this 'reload page' on desktop too and refresh doesn't fix it.")
//
// TWO different failures used to render the SAME "update was just published"
// card, which is why this was whack-a-mole:
//
//   1. CHUNK MISS (transient). Every deploy replaces the hashed chunk files in
//      _vite-assets/. A tab opened BEFORE a deploy holds OLD html whose lazy()
//      imports point at hashes that no longer exist. The next tab click 404s and
//      lazy() throws. A reload with a fresh shell (new hashes) heals it. This is
//      the ONLY case a reload fixes.
//
//   2. RENDER CRASH (deterministic). A screen throws while rendering (bad data,
//      undefined read, etc.). Reload re-runs the same code on the same data and
//      throws again, reload NEVER fixes it. Showing "update was just published"
//      + a Reload button here was a lie that sent users in circles.
//
// So we now: tell the two apart, show honest copy, offer a real in-place retry
// for render crashes (re-render without a reload, clears transient data races),
// and, critically, CAPTURE every crash (message, stack, route, build) to the
// console, a localStorage ring buffer, and window.acLastError, plus global
// window.onerror / unhandledrejection handlers. Nothing is swallowed silently
// anymore, so the actual crashing screen is finally diagnosable.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, Fragment, lazy, useEffect, useState } from 'react';
import type { ComponentType, JSX, LazyExoticComponent, ReactNode } from 'react';

const RELOADED_KEY = 'ac_chunk_reloaded';
const ERRLOG_KEY = 'ac:errlog';

// Optional server sink for client crashes. Leave '' to keep capture client-only
// (console + localStorage + window.acLastError). Point it at a worker endpoint
// (e.g. 'https://auth.artistcntrl.com/api/client-error') to also beacon crashes
// server-side, the worker just needs to accept a POST and log it.
const CLIENT_ERROR_ENDPOINT = '';

type ErrKind = 'chunk' | 'render' | 'window.onerror' | 'unhandledrejection';

interface ErrRecord {
  kind: ErrKind;
  message: string;
  stack: string;
  route: string;
  build: string;
  ua: string;
  ts: string;
}

// ── Diagnostics ────────────────────────────────────────────────────────────────

function routeNow(): string {
  try { return window.location.pathname + window.location.hash; } catch { return ''; }
}

// A cheap build fingerprint: the hashed entry script the shell loaded. Changes
// every deploy, so a crash report tells us WHICH build threw.
function buildTag(): string {
  try {
    const s = document.querySelector('script[type="module"][src]');
    return (s && s.getAttribute('src')) || '';
  } catch { return ''; }
}

/** Record a client crash everywhere useful and return the record. Never throws. */
export function captureClientError(kind: ErrKind, error: unknown): ErrRecord {
  const e = error as { message?: string; stack?: string } | null;
  const rec: ErrRecord = {
    kind,
    message: (e && (e.message || String(error))) || 'unknown error',
    stack: e && e.stack ? String(e.stack).split('\n').slice(0, 10).join('\n') : '',
    route: routeNow(),
    build: buildTag(),
    ua: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
    ts: new Date().toISOString(),
  };
  try { console.error('[ac-error]', kind, rec.message, rec); } catch { /* ignore */ }
  try {
    (window as unknown as { acLastError?: ErrRecord }).acLastError = rec;
    const raw = localStorage.getItem(ERRLOG_KEY);
    const arr: ErrRecord[] = raw ? (JSON.parse(raw) as ErrRecord[]) : [];
    arr.unshift(rec);
    localStorage.setItem(ERRLOG_KEY, JSON.stringify(arr.slice(0, 10)));
  } catch { /* storage blocked, console still fired */ }
  try {
    if (CLIENT_ERROR_ENDPOINT && typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, JSON.stringify(rec));
    }
  } catch { /* beacon best-effort */ }
  return rec;
}

// Catch async / non-render crashes too, these never reach a React error
// boundary, so before this they vanished entirely. Installed once on module load
// (lazyScreen is imported by both app entries).
let _globalInstalled = false;
function installGlobalErrorCapture(): void {
  if (_globalInstalled || typeof window === 'undefined') return;
  _globalInstalled = true;
  try {
    window.addEventListener('error', (ev: ErrorEvent) => {
      // Only real script errors, resource-load errors carry no error/message.
      if (ev && (ev.error || ev.message)) {
        captureClientError('window.onerror', ev.error || new Error(ev.message));
      }
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      const reason = ev && ev.reason;
      captureClientError('unhandledrejection', reason instanceof Error ? reason : new Error(String(reason)));
    });
  } catch { /* ignore */ }
}
installGlobalErrorCapture();

// ── Recovery UI ─────────────────────────────────────────────────────────────────

/** Copyable crash details, behind a "Details" toggle so normal users see a clean
 *  card, but anyone (Ryan, a tester) can grab the exact error to send us. */
function DetailBlock({ rec }: { rec: ErrRecord | null }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!rec) return null;
  const text = `${rec.kind} @ ${rec.route}\n${rec.message}\n${rec.stack}\nbuild:${rec.build}\n${rec.ts}`;
  return (
    <div style={{ marginTop: 6, fontSize: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{ background: 'none', border: 'none', color: 'inherit', opacity: 0.55, cursor: 'pointer', textDecoration: 'underline', font: 'inherit', padding: 0 }}
      >
        {open ? 'Hide details' : 'Details'}
      </button>
      {open ? (
        <div style={{ marginTop: 8, textAlign: 'left', maxWidth: 460 }}>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, lineHeight: 1.4, background: 'rgba(0,0,0,0.05)', padding: 10, borderRadius: 8, maxHeight: 180, overflow: 'auto' }}>{text}</pre>
          <button
            type="button"
            onClick={() => { try { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ } }}
            style={{ marginTop: 4, padding: '5px 14px', borderRadius: 999, border: '1px solid rgba(0,0,0,0.25)', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 12 }}
          >
            {copied ? 'Copied ✓' : 'Copy error'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function RecoveryCard({ kind, rec, onRetry }: { kind: 'chunk' | 'render'; rec: ErrRecord | null; onRetry?: () => void }): JSX.Element {
  /* The branded boot overlay (#acLoader) only hides when a screen's data paints,
     a crash during boot left the card INVISIBLE under the spinner. Hide it. */
  useEffect(() => {
    try {
      const w = window as unknown as { acHideLoader?: () => void };
      if (typeof w.acHideLoader === 'function') w.acHideLoader();
      document.getElementById('acLoader')?.classList.add('ac-loader--hidden');
    } catch { /* best-effort */ }
  }, []);

  const isChunk = kind === 'chunk';
  const headline = isChunk
    ? 'Finishing an update…'
    : 'Something on this screen hit an error.';
  const sub = isChunk
    ? 'A new version was just published. Reload to pick it up.'
    : 'Reloading may not clear it, try again first, and if it keeps happening, tap Details and send it to us.';

  return (
    <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, textAlign: 'center' }}>
      <p style={{ fontWeight: 700, margin: 0 }}>{headline}</p>
      <p style={{ opacity: 0.72, maxWidth: 420, lineHeight: 1.5, margin: 0 }}>{sub}</p>
      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
        {!isChunk && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            style={{ padding: '10px 26px', borderRadius: 999, border: '1px solid #111', background: '#fff', color: '#111', fontWeight: 600, cursor: 'pointer' }}
          >
            Try again
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{ padding: '10px 26px', borderRadius: 999, border: '1px solid #111', background: '#111', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
        >
          Reload
        </button>
      </div>
      <DetailBlock rec={rec} />
    </div>
  );
}

/** Backward-compatible no-arg export (used as lazyScreen's final fallback and by
 *  any external importer). Renders the chunk-flavored card. */
export function ChunkRecovery(): JSX.Element {
  const rec = (() => { try { return (window as unknown as { acLastError?: ErrRecord }).acLastError || null; } catch { return null; } })();
  return <RecoveryCard kind="chunk" rec={rec} />;
}

/** Drop-in replacement for `lazy()` on route screens: retries a failed chunk
 *  import (cache-bust refetch, then one automatic reload), then falls back to the
 *  chunk recovery card. Generic so prop-taking screens type-check. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyScreen<C extends ComponentType<any>>(
  factory: () => Promise<{ default: C }>,
): LazyExoticComponent<C> {
  return lazy(async (): Promise<{ default: C }> => {
    try {
      const mod = await factory();
      try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* storage blocked */ }
      return mod;
    } catch (err) {
      captureClientError('chunk', err);
      /* cache-poison recovery: a deploy-window miss can leave HTML cached under
         the chunk URL with immutable headers, plain reloads never revalidate it.
         Pull the failing URL out of the error, refetch with cache:'reload', and
         retry the import once (failed module fetches aren't memoized). */
      try {
        const failUrl = /module:?\s+(https?:\S+)/.exec(String((err as Error | null)?.message || ''))?.[1];
        if (failUrl) {
          await fetch(failUrl, { cache: 'reload' });
          const retried = await factory();
          try { sessionStorage.removeItem(RELOADED_KEY); } catch { /* ignore */ }
          return retried;
        }
      } catch { /* cache-refresh retry failed, fall through */ }
      try {
        if (!sessionStorage.getItem(RELOADED_KEY)) {
          sessionStorage.setItem(RELOADED_KEY, '1');
          window.location.reload();
          return { default: (() => null) as unknown as C };
        }
      } catch { /* storage blocked, fall through to the card */ }
      return { default: ChunkRecovery as unknown as C };
    }
  });
}

/** Catches RENDER crashes below it. Unlike before, it (a) captures the real error,
 *  (b) shows honest render-crash copy, NOT "update published", and (c) offers a
 *  true in-place retry that re-renders without a full reload (clears transient
 *  data-race crashes). Wrap the router's <Routes>. */
export class ScreenErrorBoundary extends Component<{ readonly children: ReactNode }, { err: boolean; rec: ErrRecord | null; nonce: number }> {
  constructor(props: { readonly children: ReactNode }) {
    super(props);
    this.state = { err: false, rec: null, nonce: 0 };
  }
  static getDerivedStateFromError(): Partial<{ err: boolean }> {
    return { err: true };
  }
  componentDidCatch(error: unknown): void {
    const rec = captureClientError('render', error);
    this.setState({ rec });
  }
  render(): ReactNode {
    if (this.state.err) {
      return (
        <RecoveryCard
          kind="render"
          rec={this.state.rec}
          onRetry={() => this.setState((s) => ({ err: false, rec: null, nonce: s.nonce + 1 }))}
        />
      );
    }
    // A keyed Fragment forces a fresh subtree on retry (crashed component remounts
    // cleanly instead of reusing the errored fiber) while adding NO DOM node, no
    // layout risk on the app root.
    return <Fragment key={this.state.nonce}>{this.props.children}</Fragment>;
  }
}
